import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { pushToast } from './useToasts';
/** Compara el snapshot anterior de jobs con el actual; cuando un job
 *  pasa de `queued|running` a `succeeded|failed`, emite:
 *    1. Un toast persistente en la UI
 *    2. Una browser Notification (si el usuario dio permiso y la pestaña
 *       NO está visible)
 *
 *  La idea es no spamear: sólo notificamos transiciones nuevas que
 *  ocurrieron mientras el usuario tenía la app abierta.
 */
export function useJobNotifications(jobs) {
    const { t } = useTranslation();
    const seen = useRef(new Map());
    // Pide permiso al montar (idempotente).
    useEffect(() => {
        if ('Notification' in window && Notification.permission === 'default') {
            void Notification.requestPermission();
        }
    }, []);
    useEffect(() => {
        for (const j of jobs) {
            const prev = seen.current.get(j.id);
            if (prev === j.status)
                continue;
            const wasOpen = prev === 'queued' || prev === 'running';
            const isTerminal = j.status === 'succeeded' || j.status === 'failed';
            if (wasOpen && isTerminal) {
                const ok = j.status === 'succeeded';
                const title = ok
                    ? t('notifications.jobDone', { kind: t(`dashboard.kind.${j.kind}`) })
                    : t('notifications.jobFailed', { kind: t(`dashboard.kind.${j.kind}`) });
                const msg = ok
                    ? t('notifications.jobDoneMsg', { id: j.id.slice(0, 8) })
                    : (j.error_message ?? '').slice(0, 160);
                pushToast({ kind: ok ? 'success' : 'error', title, msg });
                if ('Notification' in window &&
                    Notification.permission === 'granted' &&
                    document.visibilityState !== 'visible') {
                    try {
                        new Notification(title, { body: msg, tag: j.id });
                    }
                    catch { /* algunos navegadores fallan en ciertos contextos; ignoramos */ }
                }
            }
            seen.current.set(j.id, j.status);
        }
    }, [jobs, t]);
}
